import { useMemo, useState } from 'react'
import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import { useStore } from '@tanstack/react-store'
import { Upload, Workflow } from 'lucide-react'
import { toast } from 'sonner'
import { Skeleton } from 'skullet/react'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'

import { listEmails } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { AttachmentLiteSchema, type AttachmentLite } from '@/gen/cortado/v1/attachments_pb'
import { ListEmailsRequestSchema } from '@/gen/cortado/v1/emails_pb'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { useHasEmailInboxFeature } from '@/hooks/use-has-feature'
import { usePermissions } from '@/hooks/use-permissions'
import { getErrorDescription } from '@/lib/error-utils'
import { useAttachments, useDeleteAttachment } from '@/stores/attachments.store'
import { organizationStore } from '@/stores/organization.store'
import { useWorkflowExecutions } from '@/stores/workflows.store'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AttachmentList, AttachmentSheet } from '@/components/attachments'
import { ActionGate } from '@/components/shared/action-gate'
import { EntityWorkflowRuns } from '@/components/shared/entity-workflow-runs'
import { RunWorkflowDialog } from '@/components/shared/run-workflow-dialog'
import { JobActivityEmails, JobComposeButton } from '@/components/pipeline/job-activity-emails'
import { useJobEmailThreads } from '@/components/pipeline/job-emails-panel'
import { JobTabCount } from '@/components/pipeline/job-tab-count'

/** The lead's workflow runs are keyed on the inquiry, as on the lead page's Workflows tab. */
const WORKFLOW_TRIGGER_ENTITY_TYPE = 'INQUIRY' as const
/** The same page size `EntityWorkflowRuns` requests, so the tab count shares its query. */
const WORKFLOW_RUNS_PAGE_SIZE = 25

/** The legacy email history's page size, so the tab count shares its first page. */
const LEGACY_EMAILS_PAGE_SIZE = 20

/**
 * The Emails tab's total for orgs without the Connected inbox add-on: the
 * legacy `ListEmails` history's thread count, from the same request
 * `EmailHistoryList` makes for its first page.
 */
function useLegacyJobEmailCount(inquiryId: string | undefined, enabled: boolean) {
  const organizationId = useStore(organizationStore, (state) => state.selectedOrganizationId) ?? ''
  const request = useMemo(
    () =>
      create(ListEmailsRequestSchema, {
        organizationId,
        parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
        parentId: inquiryId ?? '',
        limit: LEGACY_EMAILS_PAGE_SIZE,
        offset: 0,
      }),
    [organizationId, inquiryId],
  )
  const query = useQuery(listEmails, request, { enabled: enabled && Boolean(inquiryId && organizationId) })
  return query.data?.threadTotalCount ?? query.data?.totalCount ?? 0
}

const SAMPLE_ATTACHMENTS: AttachmentLite[] = [
  { filename: 'signed-contract.pdf', contentType: 'application/pdf' },
  { filename: 'floor-plan.png', contentType: 'image/png' },
].map(({ filename, contentType }, i) =>
  create(AttachmentLiteSchema, {
    id: `sample-${i}`,
    filename,
    contentType,
    sizeBytes: 248000n,
    createdByFirstName: 'Jordan',
    createdByLastName: 'Rivera',
    createdAt: timestampFromDate(new Date(2026, 0, i + 1)),
  }),
)

/**
 * The job's linked inquiry's files (`ListAttachments`, parent INQUIRY): the
 * shared attachment list with its preview, download and delete, plus Upload.
 * Without an inquiry there is nothing to attach to, so only the list's empty
 * state shows.
 */
function JobFilesPanel({ inquiryId }: { inquiryId?: string }) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const { attachments, isLoading, hasLoaded } = useAttachments({
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: inquiryId,
    enabled: Boolean(inquiryId),
  })
  const { mutation: deleteAttachmentMutation } = useDeleteAttachment()

  async function deleteAttachment(id: string) {
    try {
      await deleteAttachmentMutation.mutateAsync({ id })
      toast.success('Attachment deleted')
    } catch (error) {
      toast.error('Failed to delete attachment', { description: getErrorDescription(error) })
    }
  }

  const canUpload = Boolean(inquiryId && selectedOrganizationId)

  return (
    <div className="px-6 py-4">
      {canUpload && (
        <div className="mb-3 flex justify-end">
          <Button type="button" size="sm" variant="outline" onClick={() => setIsUploadOpen(true)}>
            <Upload className="h-4 w-4" />
            Upload
          </Button>
        </div>
      )}
      <Skeleton
        data={inquiryId && isLoading && !hasLoaded ? null : inquiryId ? attachments : []}
        sample={SAMPLE_ATTACHMENTS}
        label="Loading files"
      >
        {(attachments) => <AttachmentList attachments={attachments} onDelete={(id) => void deleteAttachment(id)} />}
      </Skeleton>
      {canUpload && (
        <AttachmentSheet
          organizationId={selectedOrganizationId!}
          parentType={ParentTypeEnum.PARENT_TYPE_INQUIRY}
          parentId={inquiryId!}
          open={isUploadOpen}
          onOpenChange={setIsUploadOpen}
        />
      )}
    </div>
  )
}

/**
 * The job's linked inquiry's workflow runs, through the shared per-entity
 * runs panel, under a sub-header with Run workflow (the lead page's manual
 * run: AutomationsManage-gated, as there). Without an inquiry nothing can
 * have run or be run, so Run workflow is disabled and the panel shows its
 * empty state rather than an unscoped query.
 */
function JobWorkflowsPanel({ inquiryId }: { inquiryId?: string }) {
  const organizationId = useStore(organizationStore, (state) => state.selectedOrganizationId) ?? ''
  const { can } = usePermissions(organizationId)
  const canRunWorkflow = can('AutomationsManage')
  const [isRunWorkflowOpen, setIsRunWorkflowOpen] = useState(false)

  return (
    <div>
      <div className="flex items-center justify-between gap-3 border-b px-6 py-3">
        <p className="min-w-0 truncate text-sm text-muted-foreground">Run a lead workflow on this job</p>
        <ActionGate organizationId={organizationId} permission="AutomationsManage" verb="run" noun="workflows">
          <Button type="button" size="sm" onClick={() => setIsRunWorkflowOpen(true)} disabled={!inquiryId}>
            <Workflow className="h-4 w-4" />
            Run workflow
          </Button>
        </ActionGate>
      </div>
      <div className="px-6 py-4">
        {inquiryId ? (
          <EntityWorkflowRuns sourceResourceId={inquiryId} triggerEntityType={WORKFLOW_TRIGGER_ENTITY_TYPE} />
        ) : (
          // The dashed outline lives on a wrapper so Empty keeps its own shape;
          // max-md:py-6 tops Empty's narrow-screen p-6 up to its md:p-12.
          <div className="rounded-lg border border-dashed max-md:py-6">
            <Empty>
              <EmptyMedia variant="icon">
                <Workflow className="h-5 w-5" />
              </EmptyMedia>
              <EmptyDescription>No workflow runs yet</EmptyDescription>
            </Empty>
          </div>
        )}
      </div>
      {inquiryId && canRunWorkflow && (
        // StartWorkflowExecution invalidates ListWorkflowExecutions, so the new
        // run shows in the list (and the tab count) once it starts.
        <RunWorkflowDialog
          sourceResourceId={inquiryId}
          triggerEntityType={WORKFLOW_TRIGGER_ENTITY_TYPE}
          open={isRunWorkflowOpen}
          onOpenChange={setIsRunWorkflowOpen}
        />
      )}
    </div>
  )
}

/**
 * The job page's Activity card, in the main column: Emails (the default),
 * Files and Workflows, all scoped to the job's linked inquiry. Emails show as
 * inline threads with the Connected inbox add-on, else the legacy history;
 * Each tab label carries its total, so the counts are fetched up front.
 */
export function JobActivityCard({
  jobId,
  inquiryId,
  contactId,
}: {
  jobId: string
  inquiryId?: string
  /** The job's client, whose address seeds Compose's To. */
  contactId?: string
}) {
  const { hasFeature: hasEmailInbox, hasLoaded: hasLoadedFeatures } = useHasEmailInboxFeature()
  const emailsQuery = useJobEmailThreads(inquiryId)
  const legacyEmailCount = useLegacyJobEmailCount(inquiryId, hasLoadedFeatures && !hasEmailInbox)
  const emailCount = hasEmailInbox ? emailsQuery.totalCount : legacyEmailCount
  // Same requests as the Files and Workflows panels, so each count and its
  // list share one cached query.
  const { totalCount: fileCount } = useAttachments({
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: inquiryId,
    enabled: Boolean(inquiryId),
  })
  const workflowRuns = useWorkflowExecutions({
    sourceResourceId: inquiryId,
    triggerEntityType: WORKFLOW_TRIGGER_ENTITY_TYPE,
    pageSize: WORKFLOW_RUNS_PAGE_SIZE,
    enabled: Boolean(inquiryId),
  })
  const workflowCount = inquiryId
    ? `${workflowRuns.executions.length}${workflowRuns.nextPageToken ? '+' : ''}`
    : 0
  const [tab, setTab] = useState('emails')

  return (
    <Card className="gap-0 overflow-hidden pt-0">
      <Tabs value={tab} onValueChange={setTab} className="gap-0">
        <CardHeader className="gap-0 border-b py-3 [.border-b]:pb-3">
          {/* Title and tabs on the left, the Emails tab's Compose on the far right. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <CardTitle className="shrink-0 text-base">Activity</CardTitle>
            {/* -my-1: the tab bar overhangs the text line instead of growing the
                header, so it matches the other cards' headers. */}
            <TabsList className="-my-1 h-8">
              <TabsTrigger value="emails">
                Emails <JobTabCount value={inquiryId ? emailCount : 0} />
              </TabsTrigger>
              <TabsTrigger value="files">
                Files <JobTabCount value={inquiryId ? fileCount : 0} />
              </TabsTrigger>
              <TabsTrigger value="workflows">
                Workflows <JobTabCount value={workflowCount} />
              </TabsTrigger>
            </TabsList>
            {tab === 'emails' && (
              <div className="ml-auto">
                <JobComposeButton inquiryId={inquiryId} contactId={contactId} />
              </div>
            )}
          </div>
        </CardHeader>
        <div>
          <TabsContent value="emails">
            <JobActivityEmails
              jobId={jobId}
              inquiryId={inquiryId}
              contactId={contactId}
              threads={emailsQuery.threads}
              totalCount={emailsQuery.totalCount}
              isLoading={emailsQuery.isLoading}
              isError={emailsQuery.isError}
              onRetry={() => void emailsQuery.refetch()}
            />
          </TabsContent>
          <TabsContent value="files">
            <JobFilesPanel inquiryId={inquiryId} />
          </TabsContent>
          <TabsContent value="workflows">
            <JobWorkflowsPanel inquiryId={inquiryId} />
          </TabsContent>
        </div>
      </Tabs>
    </Card>
  )
}
